import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit, Check, X } from "lucide-react";
import type { PointBlock } from "@/blocks/geojson.blocks";
import { useRoom } from "@/hooks/use-room";

export default function Point({ block }: { block: PointBlock }) {
  const { docs } = useRoom();
  const [editing, setEditing] = useState(false);
  const [longitude, setLongitude] = useState(
    block.attributes.coordinates[0].toString(),
  );
  const [latitude, setLatitude] = useState(
    block.attributes.coordinates[1].toString(),
  );

  const handleSave = () => {
    if (!docs) return;

    const newLng = parseFloat(longitude);
    const newLat = parseFloat(latitude);

    // Validate coordinates
    if (isNaN(newLng) || isNaN(newLat)) {
      // Reset to original values if invalid
      setLongitude(block.attributes.coordinates[0].toString());
      setLatitude(block.attributes.coordinates[1].toString());
      return;
    }

    // Clamp longitude to -180 to 180
    const clampedLng = Math.max(-180, Math.min(180, newLng));
    // Clamp latitude to -90 to 90
    const clampedLat = Math.max(-90, Math.min(90, newLat));

    docs.updateBlockAttributes(block.id, {
      ...block.attributes,
      coordinates: [clampedLng, clampedLat],
    });

    setLongitude(clampedLng.toString());
    setLatitude(clampedLat.toString());
    setEditing(false);
  };

  const handleCancel = () => {
    setLongitude(block.attributes.coordinates[0].toString());
    setLatitude(block.attributes.coordinates[1].toString());
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 p-2 bg-orange-50 rounded border">
        <span className="text-sm font-medium text-orange-900">Point:</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-orange-700">Lat:</span>
          <Input
            type="number"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            className="w-20 h-7 text-xs"
            step="0.000001"
            min="-90"
            max="90"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-orange-700">Lng:</span>
          <Input
            type="number"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            className="w-20 h-7 text-xs"
            step="0.000001"
            min="-180"
            max="180"
          />
        </div>
        <Button size="sm" variant="outline" onClick={handleSave}>
          <Check className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="outline" onClick={handleCancel}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-2 bg-orange-50/50 rounded border">
      <span className="text-sm text-orange-900">
        <strong>Point:</strong> lat:{" "}
        {block.attributes.coordinates[1].toFixed(6)}, lng:{" "}
        {block.attributes.coordinates[0].toFixed(6)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="text-orange-700 hover:text-orange-800 hover:bg-orange-100"
      >
        <Edit className="h-3 w-3" />
      </Button>
    </div>
  );
}
